import { Children, CSSProperties, FC, isValidElement, ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { SIDER_COLLAPSE_ANIMATION_DURATION } from '../AnimatedCollapsibleContent';

interface AnimatedSiderCollectionProps {
    /** Collection children must use stable domain keys so unrelated rows never remount. */
    children: ReactNode;
    className?: string;
    collapsed?: boolean;
    style?: CSSProperties;
}

const AnimatedSiderCollection: FC<AnimatedSiderCollectionProps> = ({
    children,
    className,
    collapsed = false,
    style,
}) => {
    const reduceMotion = Boolean(useReducedMotion());
    const items = collapsed ? [] : Children.toArray(children);

    return (
        <div className={className} data-animated-sider-collection style={style}>
            <AnimatePresence initial={false}>
                {items.map((child, index) => {
                    const itemKey = isValidElement(child) && child.key !== null ? child.key : `sider-item-${index}`;

                    return (
                        <motion.div
                            data-animated-sider-item
                            key={itemKey}
                            initial={reduceMotion ? false : { height: 0, opacity: 0, y: -4 }}
                            animate={{ height: 'auto', opacity: 1, y: 0 }}
                            exit={reduceMotion ? undefined : { height: 0, opacity: 0, y: -4 }}
                            transition={{
                                duration: reduceMotion ? 0 : SIDER_COLLAPSE_ANIMATION_DURATION / 1000,
                                ease: 'easeInOut',
                            }}
                            style={{ overflow: 'hidden' }}
                        >
                            {child}
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        </div>
    );
};

export default AnimatedSiderCollection;
